// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
/* eslint-disable @typescript-eslint/no-explicit-any */
import _get from 'lodash/get';

export class Csv {
  private data: Record<string, any>[] | string[][];

  private columnDelimiter = ',';

  private lineDelimiter = '\n';

  private delimiterRegex = /[,\n\r\t]/g;

  private keys: string[] = [];

  private headers: string[] = [];

  private headNote = '';

  private bom = { char: '\uFEFF', code: 0xfeff };

  constructor(params: {
    data: Record<string, any>[] | string[][];
    headers?: (string | [string, string])[];
    headNote?: string;
  }) {
    const { data, headers, headNote } = params;

    this.data = data;

    if (headers?.length) {
      headers.forEach((header) => {
        if (Array.isArray(header)) {
          this.keys.push(header[0]);
          this.headers.push(header[1]);
        } else {
          this.keys.push(header);
          this.headers.push(header);
        }
      });
    } else if (data.length) {
      this.keys = Object.keys(this.data[0]);
      this.headers = [...this.keys];
    }

    if (headNote) {
      this.headNote = headNote;
    }
  }

  ensureBOM(csvString: string, withBOM: boolean) {
    if (!withBOM || csvString.charCodeAt(0) === this.bom.code) {
      return csvString;
    }

    return this.bom.char + csvString;
  }

  convertDataWithQuote(data: any) {
    if (typeof data === 'string' && data.match(this.delimiterRegex)) {
      return `"${data.replace(/"/g, '""')}"`;
    }

    return data;
  }

  getCsvData() {
    if (!this.data.length) {
      return [];
    }

    if (this.data.length && Array.isArray(this.data[0])) {
      return (this.data as string[][]).map((item) =>
        item
          .map((value) => this.convertDataWithQuote(value))
          .join(this.columnDelimiter)
      );
    }

    return (this.data as Record<string, any>[]).reduce<string[]>(
      (result, item) => {
        const itemData = this.keys
          .map((key) => this.convertDataWithQuote(_get(item, key)))
          .join(this.columnDelimiter);
        result.push(itemData);

        return result;
      },
      []
    );
  }

  generateCsv(params?: {
    withHeaders?: boolean;
    withBOM?: boolean;
    charset?: string;
  }) {
    const {
      withHeaders = true,
      withBOM = true,
      charset = 'utf-8',
    } = params || {};
    const csvData = this.getCsvData();

    if (withHeaders) {
      const headers = this.headers
        .map((header) => this.convertDataWithQuote(header))
        .join(this.columnDelimiter);

      csvData.unshift(headers);
    }

    const csvString = csvData.join(this.lineDelimiter);
    const finalCsvContent = this.headNote
      ? `${this.headNote}${this.lineDelimiter.repeat(2)}${csvString}`
      : csvString;

    if (charset.toLowerCase() !== 'utf-8') {
      return finalCsvContent;
    }

    return this.ensureBOM(finalCsvContent, withBOM);
  }

  downLoadCsv(params: {
    filename: string;
    withHeaders?: boolean;
    charset?: string;
    withBOM?: boolean;
  }) {
    const {
      filename,
      withHeaders = true,
      charset = 'utf-8',
      withBOM = true,
    } = params;

    const downloadEl = document.createElement('a');

    const csvData = this.generateCsv({
      withHeaders,
      withBOM,
    });

    downloadEl.href = `data:text/csv;charset=${charset},${encodeURIComponent(
      csvData
    )}`;
    downloadEl.download = `${filename}.csv`;
    downloadEl.click();

    downloadEl.remove();
  }
}

export default Csv;
